/**
 * Spotlight — the one slot on the app's Search tab that Evan writes himself.
 *
 * Evan, 2026-09-19: *"We can forget about the 'Did you know.' I'm thinking for the spotlight we
 * focus on doing things like announcements and polls where I can type whatever I want. … I want to
 * just start pulling people, random things, asking people random questions, feedback, whatever."*
 * And: *"I don't see where I can mess with the spotlight from my admin dashboard. It should be on
 * the search page tab."*
 *
 * Evan, 2026-09-21, on the fourth kind: *"a grid three venues wide of venue profiles. With a
 * little image of the venue. Then you actually just select in the grid."* A `venue_poll` is
 * composed here the same way as everything else, except that its options are PICKED rather than
 * typed — `VenuePicker`, the control the mockup and content-studio surfaces already use — and the
 * card stores only the venue ids. The names and photographs are resolved server side on every
 * read, so a venue renamed in `venues` renames its tile with nothing to re-save.
 *
 * ── THE PANEL IS BUILT FOR ONE SENTENCE: "ASK THEM SOMETHING RIGHT NOW" ──────────────────────
 * Pick a kind, type the words, choose everywhere-or-one-city, press **Publish now**. Four controls
 * and one button, all of them above the fold, and the composer is ALWAYS OPEN rather than hiding
 * behind a "New card" button — the previous version made the fastest path in the product start
 * with a click that only revealed a form. Scheduling, links, priority and the eyebrow still exist;
 * they are folded into "More options" because they are the rare deliberate case, not the default.
 * A new card starts with NO WINDOW: open-ended, live the moment you publish it.
 *
 * ── MANUAL ONLY. THE ROBOT IS GONE. ──────────────────────────────────────────────────────────
 * An earlier pass synthesised a "Did you know?" fact from production numbers whenever nothing was
 * scheduled, with a switch at the top of this panel to turn that off. Evan cut the whole idea
 * before it shipped. With nothing live, `get_search_spotlight` returns NULL and the app's slot
 * renders nothing at all — so "no card" now means one thing instead of two, and there is no
 * setting on this page.
 *
 * ── WHY THERE IS STILL A PREVIEW ─────────────────────────────────────────────────────────────
 * "Live" in the table is not "on the page": a card can be on and still invisible because its
 * window has not opened, because it belongs to another city, or because a higher-priority card is
 * outranking it. The preview runs the SAME `get_search_spotlight` the app calls, for one city, and
 * shows the single card that wins.
 *
 * ⚠️ IT DEGRADES TO A SET-UP NOTE, NOT A RED BANNER. `supabase/migrations/20260919000000_search_spotlight.sql`
 * is written but NOT applied — Evan approves schema changes separately — so every call here comes
 * back as PGRST202 / 42883 until it lands. `rpcMissing` (shared with the collections surfaces)
 * turns that into the quiet note this file renders instead of an error.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useToast } from '../lib/useToast';
import { ErrorBanner } from '../ui/ErrorBanner';
import { EmptyState } from '../ui/EmptyState';
import { Field } from '../ui/Field';
import { SkeletonRows } from '../ui/Skeleton';
import { StatCard } from '../ui/StatCard';
import { ConfirmModal } from './ConfirmModal';
import { SpotlightBoardPanel } from './SpotlightBoardPanel';
import { errText, rpcMissing } from '../lib/collectionKinds';
import {
  loadPreviewCities,
  readStoredPreviewCity,
  writeStoredPreviewCity,
  type PreviewCity,
} from '../lib/guidePreviewCities';
import { GROWTH_VENUE_COLUMNS, venueDisplayName, type GrowthVenue } from '../lib/growth';
import { VenuePicker } from './VenuePicker';

const SETUP_NOTE =
  'Spotlight is not set up on this database yet — apply ' +
  'supabase/migrations/20260919000000_search_spotlight.sql and reload. Until then the app’s Search ' +
  'tab simply renders no spotlight (the client treats the missing function as “no card”), and ' +
  'nothing on this panel can save.';

/**
 * ⚠️ FOUR KINDS, AND `fact` IS NOT ONE OF THEM. It was the automatic card's shape and it drew
 * exactly what an announcement draws. The RPC still accepts the word and rewrites it, so an old
 * browser tab cannot produce a save error — but nothing here offers it.
 *
 * ⚠️ `venue_poll` STORES VENUE IDS WHERE `poll` STORES LABELS, in the same `options` column. That
 * is the one thing to keep in mind everywhere in this file: the composer's option rows carry both
 * shapes (see {@link DraftOption}) and `save` sends only the half the kind uses.
 */
type Kind = 'announcement' | 'poll' | 'question' | 'venue_poll';

const KINDS: { kind: Kind; label: string; blurb: string; bodyHint: string; placeholder: string }[] = [
  {
    kind: 'announcement',
    label: 'Announcement',
    blurb: 'A line of copy on the search page. Read-only — nobody replies to it.',
    bodyHint: 'What you want everyone to read.',
    placeholder: 'New this week: 400 more bars in Chicago.',
  },
  {
    kind: 'poll',
    label: 'Poll',
    blurb: 'Two to twelve tappable options. Bars and percentages appear only after a reader votes.',
    bodyHint: 'The question. The options go underneath it.',
    placeholder: 'Where are you going tonight?',
  },
  {
    kind: 'venue_poll',
    label: 'Venue poll',
    blurb:
      'Three to twenty-four venues as a grid of photo tiles. Starts as one scrolling row and opens to three wide.',
    bodyHint: 'The question. The venues go underneath it — no labels to type.',
    placeholder: 'Best rooftop in Chicago?',
  },
  {
    kind: 'question',
    label: 'Question',
    blurb: 'One free-text line. They type whatever they want and you read it here.',
    bodyHint: 'Ask them anything — their answers land under “Answers” in the table below.',
    placeholder: 'What’s missing from Shado?',
  },
];

const KIND_LABEL: Record<string, string> = Object.fromEntries(KINDS.map((k) => [k.kind, k.label]));

/** `search_spotlight_cards.body` is `char_length(body) <= 280`. Mirrored so the field can count. */
const BODY_MAX = 280;
/**
 * ⚠️ 4 → 12 (Evan 2026-09-21: *"i should be able to add more options to the spotlight polls."*).
 * Kept in step with the server bound in `admin_upsert_spotlight_card`, which raises if a poll is
 * outside 2..12 — if these two ever disagree the dashboard lets you build a card the RPC refuses
 * to save, which reads as a broken Publish button.
 *
 * ⚠️ NOT UNLIMITED. Each option is a tappable row on a card meant to be about the height of one
 * Search section. Twelve is where a list stops being a poll; nine venues with pictures is the GRID
 * variant, not a longer list.
 */
const MAX_OPTIONS = 12;

/**
 * ⚠️ A GRID HAS ITS OWN BOUNDS, AND THEY ARE NOT THE LIST'S. Kept in step with the `venue_poll`
 * arm of `admin_upsert_spotlight_card` (`a venue poll needs between 3 and 24 venues`). Three is
 * the first full row of a three-wide grid; twenty-four is eight rows, which is already further
 * than the expanded grid should run inside one Search section.
 */
const MIN_VENUE_OPTIONS = 3;
const MAX_VENUE_OPTIONS = 24;

/**
 * ── THE MORNING-AFTER REMINDER ────────────────────────────────────────────────────────────────
 *
 * Evan, 2026-09-21: *"make it where I can send notifications the next morning easily to those that
 * indicated they rated certain places. reminding them to rate it."*
 *
 * ⚠️ `{venue}` IS SUBSTITUTED SERVER SIDE, PER RECIPIENT. One press covers every linked option on
 * the card, so the audience spans SEVERAL bars — a literal sentence could not be true for both
 * Rick's and Gar Bar. The token is replaced in `admin_send_spotlight_rate_reminders`.
 *
 * ⚠️ THERE IS NO SEPARATE PUSH TITLE, AND THAT IS NOT A CHOICE MADE HERE.
 * `supabase/functions/send-push-notification/index.ts:259` sends `title: "shado"` for every
 * notification Shado has ever sent. So the headline has to live inside the text, and the RPC joins
 * these two fields into the one string iOS shows. The hint under the fields says so.
 */
const DEFAULT_REMINDER_TITLE = 'How was {venue}?';
const DEFAULT_REMINDER_BODY = 'Tap to rate your night.';
/** Kept in step with the server bounds in `admin_send_spotlight_rate_reminders`. */
const REMINDER_TITLE_MAX = 80;
const REMINDER_BODY_MAX = 200;

const REMINDER_SETUP_NOTE =
  'Morning reminders are not set up on this database yet — apply ' +
  'supabase/migrations/20260921000006_spotlight_rate_reminders.sql and reload.';

const BOARD_SETUP_NOTE =
  'Tonight and Board are not set up on this database yet — apply ' +
  'supabase/migrations/20260922000002_spotlight_board.sql and reload. Until then both switches ' +
  'below read off because the card list has nothing to report, which is not the same as their ' +
  'being switched off.';

/**
 * One person who WOULD be reminded. Mirrors `admin_spotlight_rate_reminder_audience` exactly; the
 * RPC is the single definition of the audience and the send RPC calls it, so this count is a
 * promise about how many phones are about to buzz rather than an estimate made here.
 */
type ReminderRow = {
  user_id: string;
  username: string | null;
  option_id: string | null;
  venue_id: string;
  venue_name: string;
};

type CardRow = {
  id: string;
  kind: string;
  eyebrow: string | null;
  body: string;
  /** `label` on a text poll, `venue_id` on a venue poll. Never both. */
  options: { id?: string; label?: string; venue_id?: string }[];
  placeholder: string | null;
  cta_label: string | null;
  cta_url: string | null;
  city_id: string | null;
  city_name: string | null;
  starts_at: string | null;
  ends_at: string | null;
  priority: number;
  is_active: boolean;
  /** `is_active` AND inside its window — what the table means by "showing". */
  is_live: boolean;
  response_count: number;
  /**
   * ⚠️ ONE SWITCH, THREE CONSEQUENCES — and the label in the table has to name all three or it is
   * a lie by omission. `resets_daily` means "this card is about TONIGHT", and the server and the
   * app both read it that way:
   *   1. the tally clears at 08:00 America/New_York (`spotlight_poll_day`, an 8am→8am day);
   *   2. the board under it only ever shows tonight (`get_spotlight_board` filters on `poll_day`);
   *   3. the app swaps the **Top rated** chip under the search bar for a **Tonight** chip —
   *      `app/(tabs)/search/index.tsx:3082`, `spotlight != null && spotlight.resetsDaily`, and
   *      nothing else. There is deliberately no second flag for the pill.
   */
  resets_daily: boolean;
  /** The public message board under the card. Off by default; readers post anonymously or signed. */
  board_enabled: boolean;
  /** ⚠️ NON-HIDDEN messages only, across ALL nights — the RPC excludes `hidden_at is not null`. */
  board_count: number;
};

type PreviewCard = {
  id: string | null;
  kind: string;
  eyebrow: string | null;
  body: string;
  /**
   * `label` and `photo_url` are resolved SERVER SIDE for a venue poll — see
   * `_search_spotlight_payload`. The preview shows exactly what the phone receives, so it reads
   * them off the payload rather than looking anything up itself.
   */
  options: { id?: string; label?: string; count?: number; venue_id?: string; photo_url?: string | null }[];
  total: number;
  city_name: string | null;
};

type ResultsView = {
  id: string;
  kind: string;
  body: string;
  total: number;
  options: { id?: string; label?: string; count?: number; venue_id?: string }[];
  free_text: { user_id?: string; username?: string | null; free_text?: string; updated_at?: string }[];
};

/**
 * One row of the composer's option list.
 *
 * ⚠️ `label` AND `venue` ARE BOTH DISPLAY-ONLY FOR A VENUE POLL, AND NEITHER IS SAVED. The card
 * stores `{id, venue_id}` and nothing else, because a name copied onto the card is a second copy
 * that goes stale the day somebody fixes a typo in `venues` — see the migration's header. They are
 * here so the picker has something to print.
 */
type DraftOption = {
  id: string;
  /** Text poll: what the reader reads, and the only thing saved. Venue poll: cosmetic. */
  label: string;
  /** Venue poll: the only thing saved. Null on a text poll and on an unfilled slot. */
  venueId: string | null;
  /** Venue poll: the row behind `VenuePicker`'s button. Null while it is still loading. */
  venue: GrowthVenue | null;
};

type Draft = {
  id: string | null;
  kind: Kind;
  eyebrow: string;
  body: string;
  options: DraftOption[];
  placeholder: string;
  ctaLabel: string;
  ctaUrl: string;
  cityId: string;
  startsAt: string;
  endsAt: string;
  priority: string;
  /** Only meaningful while editing an existing card — the buttons decide it on a create. */
  isActive: boolean;
  /** Board on/off, decided BEFORE publishing (Evan 2026-09-27) — applied via the flags RPC
   * right after the upsert, since the upsert itself does not take it. */
  boardEnabled: boolean;
};

/**
 * ⚠️ FOUR LETTERS WAS A LATENT DUPLICATE-ID BUG ONCE POLLS COULD HAVE TWELVE OPTIONS (found
 * 2026-09-21). The old generator was `OPTION_IDS.find(x => !used.has(x)) ?? \`o${length}\``, so on
 * `[a,b,c,d,o4,o5]` you deleted `o4`, pressed Add, and got `o5` again — two options sharing one
 * id, which means one shared tally and an upsert the RPC refuses with "option ids must be
 * unique". Twenty-four letters covers both bounds outright, and {@link nextOptionId} no longer
 * trusts the length for a fallback.
 *
 * ⚠️ THE IDS THEMSELVES ARE WHAT VOTES POINT AT. Renaming a label (or swapping a venue) on an
 * existing id KEEPS the votes cast for it; changing the id orphans them. Nothing here ever
 * rewrites an existing option's id, deliberately.
 */
const OPTION_IDS = 'abcdefghijklmnopqrstuvwx'.split('');

/** The first id this card is not already using. Loops rather than guessing from the count. */
function nextOptionId(options: { id: string }[]): string {
  const used = new Set(options.map((o) => o.id));
  const free = OPTION_IDS.find((x) => !used.has(x));
  if (free) return free;
  for (let n = 0; ; n += 1) {
    const candidate = `o${n}`;
    if (!used.has(candidate)) return candidate;
  }
}

/** A venue poll opens with the first full row of the grid — three empty slots. */
function emptyVenueOptions(): DraftOption[] {
  return OPTION_IDS.slice(0, MIN_VENUE_OPTIONS).map((id) => ({
    id,
    label: '',
    venueId: null,
    venue: null,
  }));
}

/** A text poll opens with two, the fewest the RPC will save. */
function emptyTextOptions(): DraftOption[] {
  return [
    { id: 'a', label: '', venueId: null, venue: null },
    { id: 'b', label: '', venueId: null, venue: null },
  ];
}

/**
 * The venue rows behind a saved card's `venue_id`s, so `VenuePicker` can print their names.
 *
 * ⚠️ IT IS A PLAIN TABLE READ, NOT AN RPC, and that is fine here for the same reason
 * `lib/growth.ts` reads `venues` directly: the dashboard runs on the anon key under RLS, and
 * `venues` is readable. Returns [] on any failure — a composer that cannot print a name is a
 * cosmetic problem, and the `venue_id`s it is editing are unaffected.
 */
async function fetchVenuesByIds(ids: string[]): Promise<GrowthVenue[]> {
  if (ids.length === 0) return [];
  try {
    const { data, error } = await supabase
      .from('venues')
      .select(GROWTH_VENUE_COLUMNS)
      .in('id', ids);
    return error ? [] : ((data as GrowthVenue[]) ?? []);
  } catch {
    return [];
  }
}

/**
 * ⚠️ A NEW CARD HAS NO WINDOW AND NO EYEBROW. Both blank is the fast case: publish it and it is on
 * the page until you take it down. The old blank draft pre-filled "Did you know?" as the eyebrow,
 * which is the exact thing Evan asked to be rid of.
 */
function blankDraft(): Draft {
  return {
    id: null,
    kind: 'announcement',
    eyebrow: '',
    body: '',
    options: emptyTextOptions(),
    placeholder: '',
    ctaLabel: '',
    ctaUrl: '',
    cityId: '',
    startsAt: '',
    endsAt: '',
    priority: '0',
    isActive: false,
    boardEnabled: false,
  };
}

/**
 * `<input type="datetime-local">` speaks LOCAL wall-clock with no zone; the column is `timestamptz`.
 * Round-tripping through `Date` is what makes "9pm Friday" mean 9pm where Evan is rather than 9pm
 * UTC — the difference is four or five hours on exactly the kind of card that is scheduled for a
 * Friday night.
 */
function toLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(v: string): string | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function fmtWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

/** Does this draft use anything from behind the "More options" disclosure? */
function draftHasExtras(d: Draft): boolean {
  return (
    d.startsAt !== '' ||
    d.endsAt !== '' ||
    d.eyebrow.trim() !== '' ||
    d.ctaLabel.trim() !== '' ||
    d.ctaUrl.trim() !== '' ||
    d.placeholder.trim() !== '' ||
    (d.priority !== '0' && d.priority !== '')
  );
}

function draftFromRow(r: CardRow): Draft {
  const isVenue = r.kind === 'venue_poll';
  const opts: DraftOption[] = (Array.isArray(r.options) ? r.options : [])
    .map((o, i) => ({
      id: String(o?.id ?? OPTION_IDS[i] ?? `o${i}`),
      label: String(o?.label ?? ''),
      /**
       * ⚠️ THE NAME IS NOT ON THE CARD, SO IT IS NOT HERE EITHER. `editCard` fetches the venue
       * rows a beat later and fills `venue` in; until then the picker prints "Pick a venue" and
       * the row carries the id it will actually save.
       */
      venueId: typeof o?.venue_id === 'string' && o.venue_id.trim() !== '' ? o.venue_id : null,
      venue: null,
    }))
    .slice(0, isVenue ? MAX_VENUE_OPTIONS : MAX_OPTIONS);
  const fallback = isVenue ? emptyVenueOptions() : emptyTextOptions();
  const floor = isVenue ? MIN_VENUE_OPTIONS : 2;
  return {
    id: r.id,
    /* A legacy `fact` row edits as the announcement it always rendered as. */
    kind: KIND_LABEL[r.kind] ? (r.kind as Kind) : 'announcement',
    eyebrow: r.eyebrow ?? '',
    body: r.body ?? '',
    options: opts.length >= floor ? opts : fallback,
    placeholder: r.placeholder ?? '',
    ctaLabel: r.cta_label ?? '',
    ctaUrl: r.cta_url ?? '',
    cityId: r.city_id ?? '',
    startsAt: toLocalInput(r.starts_at),
    endsAt: toLocalInput(r.ends_at),
    priority: String(r.priority ?? 0),
    isActive: Boolean(r.is_active),
    boardEnabled: Boolean(r.board_enabled),
  };
}

/** `admin_list_spotlight_cards` returns a bare array now that the auto-fallback setting is gone. */
function normalizeCards(raw: unknown): CardRow[] {
  if (!Array.isArray(raw)) return [];
  return (raw as Partial<CardRow>[]).map((c) => ({
    id: String(c.id ?? ''),
    kind: String(c.kind ?? 'announcement'),
    eyebrow: c.eyebrow ?? null,
    body: String(c.body ?? ''),
    options: Array.isArray(c.options)
      ? (c.options as { id?: string; label?: string; venue_id?: string }[])
      : [],
    placeholder: c.placeholder ?? null,
    cta_label: c.cta_label ?? null,
    cta_url: c.cta_url ?? null,
    city_id: c.city_id ?? null,
    city_name: c.city_name ?? null,
    starts_at: c.starts_at ?? null,
    ends_at: c.ends_at ?? null,
    priority: typeof c.priority === 'number' ? c.priority : Number(c.priority ?? 0) || 0,
    is_active: Boolean(c.is_active),
    is_live: Boolean(c.is_live),
    response_count:
      typeof c.response_count === 'number' ? c.response_count : Number(c.response_count ?? 0) || 0,
    /*
     * ⚠️ `Boolean(undefined)` IS `false`, WHICH IS THE RIGHT ANSWER ON AN OLDER DATABASE. A server
     * that predates `20260922000002_spotlight_board.sql` omits these three keys entirely; reading
     * them as off matches the behaviour every card had before the migration, and {@link
     * SpotlightPanel} detects the missing keys separately so the switches say so rather than
     * silently claiming everything is turned off.
     */
    resets_daily: Boolean(c.resets_daily),
    board_enabled: Boolean(c.board_enabled),
    board_count: typeof c.board_count === 'number' ? c.board_count : Number(c.board_count ?? 0) || 0,
  }));
}

/**
 * The on/off control this dashboard has always drawn — lifted out of the table's "Live" cell now
 * that there are three of them in a row. Same markup, same colours; a second visual idea for the
 * same gesture is how a screen stops looking like one screen.
 */
function ToggleSwitch({
  on,
  disabled,
  label,
  title,
  onToggle,
}: {
  on: boolean;
  disabled: boolean;
  /** Read aloud, and the only description a screen reader gets — say which card and which switch. */
  label: string;
  /** The long version, on hover. The column heading has room for one word and no more. */
  title?: string;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      title={title}
      disabled={disabled}
      onClick={onToggle}
      className={`flex h-7 w-12 shrink-0 items-center rounded-full border px-0.5 transition-colors focus:outline-none focus:ring-2 focus:ring-accent disabled:opacity-50 ${
        on
          ? 'justify-end border-emerald-600/70 bg-toggleOn shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08)]'
          : 'justify-start border-zinc-600/80 bg-toggleOff'
      }`}
    >
      <span className={`h-5 w-5 shrink-0 rounded-full shadow-md ${on ? 'bg-white' : 'bg-zinc-300'}`} />
    </button>
  );
}

function SetupNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="max-w-3xl rounded-md border border-border/90 bg-white/[0.025] px-3 py-2 text-[11px] leading-relaxed text-muted">
      {children}
    </div>
  );
}

export function SpotlightPanel() {
  const [cards, setCards] = useState<CardRow[]>([]);
  const [cities, setCities] = useState<PreviewCity[]>([]);
  const [cityId, setCityId] = useState<string>(readStoredPreviewCity);
  const [preview, setPreview] = useState<PreviewCard | null>(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [setupNote, setSetupNote] = useState<string | null>(null);
  /** ⚠️ NEVER NULL. The composer is the page, not a modal the page can be without. */
  const [draft, setDraft] = useState<Draft>(blankDraft);
  /**
   * ⚠️ THE DISCLOSURE IS ITS OWN STATE, NOT A FUNCTION OF THE DRAFT. `open={draftHasExtras(draft)}`
   * reads beautifully and behaves badly: React writes the attribute whenever that expression
   * flips, so clearing the link you just typed would SLAM THE PANEL SHUT while you are editing
   * inside it. It opens on its own only when an edit loads a card that already uses one of these
   * fields; after that it is the reader's.
   */
  const [extrasOpen, setExtrasOpen] = useState(false);
  const [results, setResults] = useState<ResultsView | null>(null);
  const [pendingDelete, setPendingDelete] = useState<CardRow | null>(null);
  /**
   * Which card's board is open below the table. An ID rather than the row itself, so the panel
   * re-reads its card out of `cards` on every render and cannot keep drawing "Board is on" from a
   * snapshot taken before the switch beside it was flipped.
   */
  const [boardCardId, setBoardCardId] = useState<string | null>(null);
  /**
   * Set when the database predates the board migration, detected at LOAD rather than on the first
   * click. Both switches would otherwise sit there reading "off" — which is a sentence about the
   * card, not about the server, and the difference is the whole reason Evan would press one.
   */
  const [boardSetupNote, setBoardSetupNote] = useState<string | null>(null);
  /**
   * ── THE MORNING REMINDER, SCOPED TO WHICHEVER CARD'S ANSWERS ARE OPEN ──────────────────────
   *
   * ⚠️ `null` MEANS "NOT WORKED OUT YET" AND `[]` MEANS "NOBODY". They are drawn differently and
   * they have to be: an empty audience is the normal state of a card whose options are all
   * unlinked, and showing "0 people" while the query is still in flight would make Evan press
   * nothing when there was somebody to reach.
   */
  const [reminderAudience, setReminderAudience] = useState<ReminderRow[] | null>(null);
  const [reminderBusy, setReminderBusy] = useState(false);
  const [reminderNote, setReminderNote] = useState<string | null>(null);
  const [reminderTitle, setReminderTitle] = useState(DEFAULT_REMINDER_TITLE);
  const [reminderBody, setReminderBody] = useState(DEFAULT_REMINDER_BODY);
  /** How many actually went, after a send. Null until one happens. */
  const [reminderSent, setReminderSent] = useState<number | null>(null);
  const [confirmSend, setConfirmSend] = useState(false);
  const { showError, showSuccess, toastNode } = useToast();

  /** Back to an empty composer — after a save, after deleting what you were editing, or on Cancel. */
  const resetDraft = useCallback(() => {
    setDraft(blankDraft());
    setExtrasOpen(false);
  }, []);

  /**
   * Load a card into the composer, opening the rare half only if that card already uses it.
   *
   * ⚠️ THE FORM IS POPULATED SYNCHRONOUSLY AND THE VENUE NAMES ARRIVE AFTER. The card carries
   * `venue_id` and nothing else (by design — see the migration), so the picker's button text has
   * to be looked up. Waiting for that before showing the form would make Edit feel broken on a
   * slow connection for a field nobody has touched yet.
   *
   * ⚠️ THE LATE `setDraft` GUARDS ON THE CARD ID. Evan can press Edit on a second row, or Cancel,
   * while this is in flight — writing the first card's venues into whatever is in the composer
   * now is the one way this could lose work.
   */
  const editCard = useCallback(async (c: CardRow) => {
    const d = draftFromRow(c);
    setDraft(d);
    setExtrasOpen(draftHasExtras(d));
    window.scrollTo({ top: 0, behavior: 'smooth' });
    /**
     * ⚠️ A TEXT POLL NEEDS THIS LOOKUP TOO NOW. Its options may carry an OPTIONAL `venue_id` (the
     * morning-reminder link), and like a venue poll the card stores the reference and nothing
     * else — so without this the picker on a linked row would read "Pick a venue" as though the
     * link had been lost, and the first save would look like it had silently dropped it.
     */
    if (c.kind !== 'venue_poll' && c.kind !== 'poll') return;
    const ids = d.options.map((o) => o.venueId).filter((x): x is string => x !== null);
    if (ids.length === 0) return;
    const rows = await fetchVenuesByIds(ids);
    if (rows.length === 0) return;
    const byId = new Map(rows.map((v) => [v.id, v]));
    /**
     * ⚠️ `label` IS ONLY REWRITTEN FOR A VENUE POLL. On a venue grid the label is cosmetic — the
     * tile's words come from `venues` at read time — but on a TEXT poll the label is the answer
     * people tap and the only thing that is stored. Overwriting "Rick's" with the venue row's
     * "Rick's American Cafe" the moment you press Edit would rename his poll behind his back, and
     * the next save would persist it.
     */
    const isVenueGrid = c.kind === 'venue_poll';
    setDraft((prev) => {
      if (prev.id !== c.id) return prev;
      return {
        ...prev,
        options: prev.options.map((o) => {
          const v = o.venueId ? byId.get(o.venueId) : undefined;
          if (!v) return o;
          return isVenueGrid ? { ...o, venue: v, label: venueDisplayName(v) } : { ...o, venue: v };
        }),
      };
    });
  }, []);

  /**
   * ⚠️ SWITCHING INTO OR OUT OF `venue_poll` REPLACES THE OPTIONS RATHER THAN KEEPING THEM. The
   * two kinds store different things in the same array — labels versus venue ids — so carrying
   * three typed labels into a venue grid would leave three slots that LOOK filled and save
   * nothing. Switching between the three text kinds keeps whatever is there, as it always did.
   */
  const selectKind = useCallback((next: Kind) => {
    setDraft((d) => {
      if (d.kind === next) return d;
      if (next === 'venue_poll') return { ...d, kind: next, options: emptyVenueOptions() };
      if (d.kind === 'venue_poll') return { ...d, kind: next, options: emptyTextOptions() };
      return { ...d, kind: next };
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: e } = await supabase.rpc('admin_list_spotlight_cards');
      if (e) {
        if (rpcMissing(e.message, 'admin_list_spotlight_cards')) {
          setSetupNote(SETUP_NOTE);
          setCards([]);
          return;
        }
        throw new Error(e.message);
      }
      setSetupNote(null);
      setCards(normalizeCards(data));
      /*
       * ⚠️ THE KEY'S PRESENCE, NOT ITS VALUE. `board_enabled: false` and no `board_enabled` key at
       * all both normalise to `false`, and only one of them means "this database cannot do boards".
       * Looked for on the raw payload before normalisation flattens the two together.
       */
      const first = Array.isArray(data) && data.length > 0 ? (data[0] as object) : null;
      setBoardSetupNote(first && !('board_enabled' in first) ? BOARD_SETUP_NOTE : null);
    } catch (e: unknown) {
      setError(errText(e, 'Could not load the spotlight cards'));
      setCards([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * What a reader in one city receives right now — the SAME RPC the app calls.
   *
   * ⚠️ IT PASSES THE CITY ID, NOT COORDINATES. The app has coordinates and no city id, so the RPC
   * accepts both and resolves the nearest featured city from a position. From here the city is
   * already chosen, and handing it the id skips the resolution step that could otherwise land one
   * city over and quietly preview the wrong page.
   */
  const runPreview = useCallback(async (id: string) => {
    setPreviewBusy(true);
    try {
      const { data, error: e } = await supabase.rpc('get_search_spotlight', {
        p_city_id: id || null,
        p_lat: null,
        p_lng: null,
      });
      if (e) {
        setPreview(null);
        return;
      }
      setPreview(data && typeof data === 'object' ? (data as PreviewCard) : null);
    } catch {
      setPreview(null);
    } finally {
      setPreviewBusy(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      await load();
      try {
        const list = await loadPreviewCities();
        if (cancelled) return;
        setCities(list);
        const stored = readStoredPreviewCity();
        const chosen = list.find((c) => c.id === stored) ?? list[0] ?? null;
        if (chosen) {
          setCityId(chosen.id);
          writeStoredPreviewCity(chosen.id);
          await runPreview(chosen.id);
        }
      } catch {
        /* The city list is a convenience; the card table is the page and it already loaded. */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [load, runPreview]);

  const selectCity = useCallback(
    (id: string) => {
      setCityId(id);
      writeStoredPreviewCity(id);
      void runPreview(id);
    },
    [runPreview],
  );

  const refreshAll = useCallback(async () => {
    await load();
    if (cityId) await runPreview(cityId);
  }, [cityId, load, runPreview]);

  const toggleLive = useCallback(
    async (row: CardRow, next: boolean) => {
      setSaving(true);
      try {
        const { error: e } = await supabase.rpc('admin_upsert_spotlight_card', {
          p_id: row.id,
          p_kind: row.kind,
          p_eyebrow: row.eyebrow,
          p_body: row.body,
          p_options: row.options ?? [],
          p_placeholder: row.placeholder,
          p_cta_label: row.cta_label,
          p_cta_url: row.cta_url,
          p_city_id: row.city_id,
          p_starts_at: row.starts_at,
          p_ends_at: row.ends_at,
          p_priority: row.priority,
          p_is_active: next,
        });
        if (e) throw new Error(e.message);
        await refreshAll();
      } catch (e: unknown) {
        showError(errText(e, 'Could not change that card'));
      } finally {
        setSaving(false);
      }
    },
    [refreshAll, showError],
  );

  /**
   * The Tonight and Board switches.
   *
   * ⚠️ `admin_set_spotlight_card_flags` IS A SET, NOT A PATCH. Its body is a plain
   * `update … set resets_daily = coalesce(p_resets_daily,false), board_enabled = coalesce(p_board_enabled,false)`
   * — so omitting the argument you are not touching does not leave it alone, it turns it OFF.
   * Both booleans therefore go up on every call, and the one being flipped is the only one read
   * from `next`. Verified against `pg_get_functiondef` on production 2026-09-22.
   *
   * ⚠️ `??` AND NOT `||`. These are booleans and `false || row.x` is `row.x`, which would make
   * every switch-off a no-op that silently re-saved the old value. `??` only falls through on
   * `undefined`, which is exactly "the caller did not name this one".
   *
   * ⚠️ IT DOES NOT GO THROUGH `admin_upsert_spotlight_card` like the Live toggle does. That RPC
   * rewrites the whole card from the row it is handed and knows nothing about these two columns —
   * routing a switch through it would blank them on every flip of anything else.
   */
  const setFlags = useCallback(
    async (row: CardRow, next: { resetsDaily?: boolean; boardEnabled?: boolean }) => {
      setSaving(true);
      try {
        const { error: e } = await supabase.rpc('admin_set_spotlight_card_flags', {
          p_card_id: row.id,
          p_resets_daily: next.resetsDaily ?? row.resets_daily,
          p_board_enabled: next.boardEnabled ?? row.board_enabled,
        });
        if (e) {
          if (rpcMissing(e.message, 'admin_set_spotlight_card_flags')) {
            setBoardSetupNote(BOARD_SETUP_NOTE);
            return;
          }
          throw new Error(e.message);
        }
        await refreshAll();
      } catch (e: unknown) {
        showError(errText(e, 'Could not change that switch'));
      } finally {
        setSaving(false);
      }
    },
    [refreshAll, showError],
  );

  /**
   * ⚠️ `activate` IS A PARAMETER, NOT `draft.isActive`. "Publish now" and "Save as draft" are the
   * same write with a different answer to one question, and making the button say which removes
   * the checkbox that used to sit between Evan and a live card.
   */
  const save = useCallback(
    async (activate: boolean) => {
      setSaving(true);
      try {
        /**
         * ⚠️ ONLY THE REFERENCE GOES UP FOR A VENUE POLL — `{id, venue_id}`. `label` and the
         * `GrowthVenue` behind the picker are composer state and are deliberately dropped here:
         * the RPC rebuilds the stored array to exactly these two keys anyway, and sending a name
         * it would throw away only invites somebody to start depending on it.
         */
        const options =
          draft.kind === 'poll'
            ? draft.options
                .filter((o) => o.label.trim() !== '')
                /**
                 * ⚠️ `venue_id` IS OMITTED, NOT SENT AS NULL, WHEN THERE IS NO LINK. The RPC
                 * stores the key only when it is present, so an unlinked card saved after this
                 * change is byte-identical to one saved before it — "unlinked" stays the absence
                 * of a fact rather than a stored null somebody later has to interpret.
                 */
                .map((o) =>
                  o.venueId
                    ? { id: o.id, label: o.label.trim(), venue_id: o.venueId }
                    : { id: o.id, label: o.label.trim() },
                )
            : draft.kind === 'venue_poll'
              ? draft.options
                  .filter((o) => o.venueId !== null)
                  .map((o) => ({ id: o.id, venue_id: o.venueId }))
              : [];
        const { data: upserted, error: e } = await supabase.rpc('admin_upsert_spotlight_card', {
          p_id: draft.id,
          p_kind: draft.kind,
          p_eyebrow: draft.eyebrow.trim() || null,
          p_body: draft.body.trim(),
          p_options: options,
          p_placeholder: draft.placeholder.trim() || null,
          p_cta_label: draft.ctaLabel.trim() || null,
          p_cta_url: draft.ctaUrl.trim() || null,
          p_city_id: draft.cityId || null,
          p_starts_at: fromLocalInput(draft.startsAt),
          p_ends_at: fromLocalInput(draft.endsAt),
          p_priority: Number(draft.priority) || 0,
          p_is_active: activate,
        });
        if (e) throw new Error(e.message);
        /* The upsert returns {ok, id}; the board switch rides the existing flags RPC so no SQL
           changed. resets_daily must be passed too — preserve whatever the row already has. */
        const savedId = (upserted as { id?: string } | null)?.id ?? draft.id;
        const existing = draft.id ? cards.find((c) => c.id === draft.id) : undefined;
        const wasBoard = Boolean(existing?.board_enabled);
        if (savedId && draft.boardEnabled !== wasBoard) {
          const { error: fe } = await supabase.rpc('admin_set_spotlight_card_flags', {
            p_card_id: savedId,
            p_resets_daily: Boolean(existing?.resets_daily),
            p_board_enabled: draft.boardEnabled,
          });
          if (fe) showError(`Card saved, but the Board switch did not apply: ${fe.message}`);
        }
        const scheduled = draft.startsAt !== '' || draft.endsAt !== '';
        showSuccess(
          activate
            ? scheduled
              ? 'Saved and switched on — it appears inside its window'
              : 'Live on the search page now'
            : 'Saved as a draft — nobody sees it yet',
        );
        resetDraft();
        await refreshAll();
      } catch (e: unknown) {
        showError(errText(e, 'Could not save that card'));
      } finally {
        setSaving(false);
      }
    },
    [draft, cards, refreshAll, resetDraft, showError, showSuccess],
  );

  const doDelete = useCallback(async () => {
    if (!pendingDelete) return;
    setSaving(true);
    try {
      const { error: e } = await supabase.rpc('admin_delete_spotlight_card', { p_id: pendingDelete.id });
      if (e) throw new Error(e.message);
      showSuccess('Card deleted');
      if (results?.id === pendingDelete.id) {
        /* Inlined rather than calling `closeResults`, which is declared further down — putting it
         * in this dependency array would read it in the temporal dead zone on every render. */
        setResults(null);
        setReminderAudience(null);
        setReminderSent(null);
        setReminderNote(null);
      }
      /* Editing the row that just went away would leave a form pointing at nothing. */
      if (draft.id === pendingDelete.id) resetDraft();
      /* Same for the board below it — the card is gone and so are its messages. */
      if (boardCardId === pendingDelete.id) setBoardCardId(null);
      setPendingDelete(null);
      await refreshAll();
    } catch (e: unknown) {
      showError(errText(e, 'Could not delete that card'));
    } finally {
      setSaving(false);
    }
  }, [boardCardId, draft.id, pendingDelete, refreshAll, resetDraft, results, showError, showSuccess]);

  /**
   * Who would be reminded if Evan pressed the button right now.
   *
   * ⚠️ IT IS THE SERVER'S ANSWER, NOT A COUNT COMPUTED HERE FROM THE RESULTS TABLE. The RPC
   * excludes anyone who has already logged the venue since they answered, anyone already
   * reminded for this card, suspended accounts, and anyone whose notification switches say no —
   * none of which this panel can see. `admin_send_spotlight_rate_reminders` sends to exactly the
   * rows this returns, by calling the same function, so the number on the button cannot drift
   * from what actually happens.
   *
   * ⚠️ IT DEGRADES TO A NOTE, NOT AN ERROR BANNER, exactly as the rest of this panel does while
   * its migration is unapplied — Evan approves schema changes separately.
   */
  const loadReminderAudience = useCallback(async (cardId: string) => {
    setReminderBusy(true);
    setReminderNote(null);
    try {
      const { data, error: e } = await supabase.rpc('admin_spotlight_rate_reminder_audience', {
        p_card_id: cardId,
      });
      if (e) {
        if (rpcMissing(e.message, 'admin_spotlight_rate_reminder_audience')) {
          setReminderAudience([]);
          setReminderNote(REMINDER_SETUP_NOTE);
          return;
        }
        throw new Error(e.message);
      }
      setReminderAudience(Array.isArray(data) ? (data as ReminderRow[]) : []);
    } catch (e: unknown) {
      setReminderAudience([]);
      setReminderNote(errText(e, 'Could not work out who would be reminded'));
    } finally {
      setReminderBusy(false);
    }
  }, []);

  const openResults = useCallback(
    async (row: CardRow) => {
      try {
        const { data, error: e } = await supabase.rpc('admin_spotlight_card_results', {
          p_id: row.id,
          p_limit: 200,
        });
        if (e) throw new Error(e.message);
        setResults(data as ResultsView);
        /* A fresh card means a fresh audience and no "sent N" from the card before it. */
        setReminderAudience(null);
        setReminderSent(null);
        setReminderNote(null);
        void loadReminderAudience(row.id);
      } catch (e: unknown) {
        showError(errText(e, 'Could not load the answers'));
      }
    },
    [loadReminderAudience, showError],
  );

  const closeResults = useCallback(() => {
    setResults(null);
    setReminderAudience(null);
    setReminderSent(null);
    setReminderNote(null);
  }, []);

  /**
   * ⚠️ PRESSING THIS TWICE CANNOT SEND TWICE, AND NOT BECAUSE OF ANYTHING ON THIS PAGE. The RPC
   * writes a ledger row per recipient under `unique (card_id, user_id)` and only the rows that
   * ACTUALLY inserted produce a notification — so a second press (or a double click, or two open
   * browser tabs) returns 0 and nobody's phone buzzes again. The button's disabled state is a
   * courtesy on top of that, not the guard.
   */
  const sendReminders = useCallback(async () => {
    if (!results) return;
    setReminderBusy(true);
    try {
      const { data, error: e } = await supabase.rpc('admin_send_spotlight_rate_reminders', {
        p_card_id: results.id,
        p_title: reminderTitle.trim(),
        p_body: reminderBody.trim(),
      });
      if (e) throw new Error(e.message);
      const n = typeof data === 'number' ? data : Number(data ?? 0) || 0;
      setReminderSent(n);
      setConfirmSend(false);
      showSuccess(n === 1 ? 'Sent 1 reminder' : `Sent ${n} reminders`);
      /* Re-read the audience so the button immediately reflects the ledger it just wrote. */
      await loadReminderAudience(results.id);
    } catch (e: unknown) {
      showError(errText(e, 'Could not send those reminders'));
    } finally {
      setReminderBusy(false);
    }
  }, [loadReminderAudience, reminderBody, reminderTitle, results, showError, showSuccess]);

  const liveCount = useMemo(() => cards.filter((c) => c.is_live).length, [cards]);
  const scheduledCount = useMemo(() => cards.filter((c) => c.is_active && !c.is_live).length, [cards]);
  const answerCount = useMemo(() => cards.reduce((n, c) => n + c.response_count, 0), [cards]);
  /** Board messages people can currently see, across every card. Hidden ones are not counted. */
  const boardMessageCount = useMemo(() => cards.reduce((n, c) => n + c.board_count, 0), [cards]);
  /**
   * ⚠️ RESOLVED OUT OF `cards` EVERY RENDER, NOT STORED. The board panel's header states whether
   * the board is on and whether it resets — flip either switch with the panel open and it has to
   * change under you, which a captured row would not.
   */
  const boardCard = useMemo(
    () => (boardCardId ? (cards.find((c) => c.id === boardCardId) ?? null) : null),
    [boardCardId, cards],
  );
  const disabled = saving || setupNote !== null;
  const cityName = cities.find((c) => c.id === cityId)?.name ?? null;
  const spec = KINDS.find((k) => k.kind === draft.kind) ?? KINDS[0];

  /** A poll with one bar is a statement with a button — the RPC refuses it and so does this. */
  const filledOptions = useMemo(
    () => draft.options.filter((o) => o.label.trim() !== '').length,
    [draft.options],
  );
  const pickedVenues = useMemo(
    () => draft.options.filter((o) => o.venueId !== null).length,
    [draft.options],
  );
  /**
   * ⚠️ THE DUPLICATE CHECK IS HERE AS WELL AS IN THE RPC, AND IT IS THE ONLY VALIDATION IN THIS
   * FILE ABOUT MEANING RATHER THAN SHAPE. Two tiles of the same bar are two answers to one
   * question: they split its vote and neither number is true. The picker makes it easy to do by
   * accident twenty tiles down, where "the same venue is on this card more than once" arriving as
   * a save error is a worse way to find out than a disabled button and a sentence.
   */
  const duplicateVenue = useMemo(() => {
    const ids = draft.options.map((o) => o.venueId).filter((x): x is string => x !== null);
    return new Set(ids).size !== ids.length;
  }, [draft.options]);
  /**
   * How many options on the OPEN card carry a venue link. This is the precondition for the whole
   * reminder idea, and it is the difference between "nobody qualifies yet" and "this card cannot
   * ever remind anybody" — two zeroes that need two different sentences.
   */
  const linkedOptionCount = useMemo(
    () =>
      (results?.options ?? []).filter(
        (o) => typeof o.venue_id === 'string' && o.venue_id.trim() !== '',
      ).length,
    [results],
  );
  /** The audience broken down by venue, so a number Evan is about to act on is explainable. */
  const reminderByVenue = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of reminderAudience ?? []) {
      counts.set(r.venue_name, (counts.get(r.venue_name) ?? 0) + 1);
    }
    /* `Array.from`, not a spread: this project's tsconfig targets below ES2015 downlevel
     * iteration, and spreading a Map iterator is a compile error here (TS2802). */
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [reminderAudience]);
  const reminderCount = reminderAudience?.length ?? 0;
  /**
   * Exactly what one person will read, `{venue}` and all.
   * ⚠️ IT MIRRORS THE RPC'S JOIN: `btrim(title) || ' ' || btrim(body)`, trimmed. If that ever
   * changes, this changes with it — a preview that lies is worse than no preview.
   */
  const reminderPreview = [reminderTitle.trim(), reminderBody.trim()]
    .filter((s) => s !== '')
    .join(' ')
    .replace(/\{venue\}/g, reminderAudience?.[0]?.venue_name ?? 'the venue');

  const ready =
    draft.body.trim() !== '' &&
    (draft.kind !== 'poll' || filledOptions >= 2) &&
    (draft.kind !== 'venue_poll' || (pickedVenues >= MIN_VENUE_OPTIONS && !duplicateVenue)) &&
    !disabled;

  const hasSchedule = draft.startsAt !== '' || draft.endsAt !== '';

  return (
    <div className="admin-page !pt-6 max-w-6xl mx-auto">
      {toastNode}

      {error ? <ErrorBanner className="mb-5">{error}</ErrorBanner> : null}
      {setupNote ? <div className="mb-5"><SetupNote>{setupNote}</SetupNote></div> : null}

      {/* ══ THE COMPOSER — always open, and the first thing on the page ═══════════════════════ */}
      <section className="mb-7 rounded-xl border border-accent/40 bg-surface p-5 shadow-highlight">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <h2 className="admin-label-primary text-base">
            {draft.id ? 'Editing a card' : 'Write the spotlight'}
          </h2>
          <span className="text-[11px] text-mutedDim">
            {draft.id
              ? 'Saving overwrites the card on the search page.'
              : 'Whatever you type here is what people see on the app’s Search tab.'}
          </span>
          {draft.id ? (
            <button
              type="button"
              className="admin-btn admin-btn-ghost admin-btn-sm ml-auto"
              onClick={resetDraft}
            >
              Cancel edit
            </button>
          ) : null}
          <button
            type="button"
            className={`admin-btn admin-btn-secondary admin-btn-sm ${draft.id ? '' : 'ml-auto'}`}
            disabled={loading}
            onClick={() => void refreshAll()}
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {/* Kind — three buttons, not a <select>: one click instead of two. */}
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {KINDS.map((k) => (
            <button
              key={k.kind}
              type="button"
              className="admin-pill !h-8 !px-3 !text-[12px]"
              data-active={draft.kind === k.kind}
              onClick={() => selectKind(k.kind)}
            >
              {k.label}
            </button>
          ))}
          <span className="text-[11px] leading-relaxed text-mutedDim">{spec.blurb}</span>
        </div>

        <Field label={draft.kind === 'announcement' ? 'Say it' : 'Ask it'} hint={`${spec.bodyHint} ${draft.body.length}/${BODY_MAX}. ⌘/Ctrl + Enter publishes.`}>
          <textarea
            value={draft.body}
            onChange={(e) => setDraft({ ...draft, body: e.target.value.slice(0, BODY_MAX) })}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && ready) void save(true);
            }}
            rows={2}
            placeholder={spec.placeholder}
            className="admin-input !text-[14px]"
          />
        </Field>

        {draft.kind === 'poll' ? (
          <div className="mt-4">
            <Field
              label="Options"
              hint={
                <>
                  Two to twelve. ⚠️ Renaming a label keeps the votes; deleting an option retires its
                  bar and the votes cast for it stop being counted.
                  <br />
                  {/*
                    ── THE OPTIONAL LINK, EXPLAINED WHERE IT IS USED ──────────────────────────
                    ⚠️ IT CHANGES NOTHING ON THE PHONE and the hint has to say so, because a
                    control that sits next to the label looks like it edits the label. The card
                    still draws the words typed on the left; the link only decides who can be
                    reminded the next morning, under "Answers".
                  */}
                  <span className="text-muted">
                    Linking a venue is optional and changes nothing people see — the option still
                    shows the words you typed. It only lets you send “How was it? Rate it.” the
                    next morning to whoever picked it. Leave “Downtown” and “House Party” unlinked.
                  </span>
                </>
              }
            >
              <div className="space-y-2">
                {draft.options.map((o, i) => (
                  <div key={o.id} className="flex items-center gap-2">
                    <span className="w-5 shrink-0 text-center text-[11px] text-mutedDim">{o.id}</span>
                    <input
                      value={o.label}
                      onChange={(e) => {
                        const next = draft.options.slice();
                        next[i] = { ...o, label: e.target.value };
                        setDraft({ ...draft, options: next });
                      }}
                      /* `min-w-0 flex-1`: `.admin-input` is `width: 100%`, and in a flex row with
                       * the picker beside it a 100%-wide item refuses to shrink past its content
                       * without `min-w-0`, which pushes the picker off the end on a narrow pane. */
                      className="admin-input min-w-0 flex-1"
                      placeholder={`Option ${i + 1}`}
                    />
                    {/*
                      ⚠️ THE SAME `VenuePicker` THE VENUE GRID AND THE CONTENT STUDIO USE. A second
                      venue search in one dashboard would be a second set of rules about which
                      venues are offerable, and they would drift. It inherits that control's limit:
                      RATED venues only (`searchRatedVenues` filters `venue_shado_unique_users >=
                      1`), which is a real constraint on what can be linked and is not chosen here.
                    */}
                    <div className="shrink-0">
                      <VenuePicker
                        current={o.venue}
                        onPick={(v) => {
                          const next = draft.options.slice();
                          /* ⚠️ `label` UNTOUCHED — on a text poll it is the answer, not a caption. */
                          next[i] = { ...o, venueId: v.id, venue: v };
                          setDraft({ ...draft, options: next });
                        }}
                      />
                    </div>
                    {o.venueId ? (
                      <button
                        type="button"
                        className="admin-btn admin-btn-ghost admin-btn-sm shrink-0 !px-2"
                        title="Unlink this venue"
                        onClick={() => {
                          const next = draft.options.slice();
                          next[i] = { ...o, venueId: null, venue: null };
                          setDraft({ ...draft, options: next });
                        }}
                      >
                        Unlink
                      </button>
                    ) : null}
                    {/* A saved link whose venue row has not arrived yet — say so rather than
                        letting the picker read "Pick a venue" as though it had been lost. */}
                    {o.venueId && !o.venue ? (
                      <span className="shrink-0 text-[11px] text-amber-400">
                        venue {o.venueId.slice(0, 8)}…
                      </span>
                    ) : null}
                    {draft.options.length > 2 ? (
                      <button
                        type="button"
                        className="admin-btn admin-btn-ghost admin-btn-sm !px-2"
                        onClick={() =>
                          setDraft({ ...draft, options: draft.options.filter((_, j) => j !== i) })
                        }
                      >
                        ✕
                      </button>
                    ) : null}
                  </div>
                ))}
                {draft.options.length < MAX_OPTIONS ? (
                  <button
                    type="button"
                    className="admin-btn admin-btn-secondary admin-btn-sm"
                    onClick={() =>
                      setDraft({
                        ...draft,
                        options: [
                          ...draft.options,
                          { id: nextOptionId(draft.options), label: '', venueId: null, venue: null },
                        ],
                      })
                    }
                  >
                    Add option
                  </button>
                ) : null}
              </div>
            </Field>
          </div>
        ) : null}

        {/*
          ── THE VENUE GRID'S OPTIONS: A PICKER PER TILE, NO LABELS TO TYPE ────────────────────
          ⚠️ IT REUSES `VenuePicker`, THE CONTROL THE MOCKUP AND CONTENT-STUDIO SURFACES ALREADY
          USE. A second venue search in the same dashboard would be a second set of rules about
          which venues are offerable, and they would drift.

          ⚠️ THAT PICKER OFFERS **RATED** VENUES ONLY (`searchRatedVenues` filters
          `venue_shado_unique_users >= 1`). That is a real limit on what can go on a card and it is
          inherited, not chosen here — if Evan wants to poll a bar nobody has logged yet, that is a
          change to `lib/growth.ts`, which this file does not own.
        */}
        {draft.kind === 'venue_poll' ? (
          <div className="mt-4">
            <Field
              label="Venues"
              hint={`Three to twenty-four. They appear left to right — the first three are the row people see before they expand it. ⚠️ Swapping a venue on an existing tile KEEPS the votes cast for that tile; removing the tile retires them.`}
            >
              <div className="space-y-2">
                {draft.options.map((o, i) => (
                  <div key={o.id} className="flex items-center gap-3">
                    <span className="w-5 shrink-0 text-center text-[11px] text-mutedDim">
                      {i + 1}
                    </span>
                    <VenuePicker
                      current={o.venue}
                      onPick={(v) => {
                        const next = draft.options.slice();
                        next[i] = { ...o, venueId: v.id, venue: v, label: venueDisplayName(v) };
                        setDraft({ ...draft, options: next });
                      }}
                    />
                    {/* A saved id whose row did not come back — the save would fail, so say so. */}
                    {o.venueId && !o.venue ? (
                      <span className="text-[11px] text-amber-400">
                        {o.label || `venue ${o.venueId.slice(0, 8)}…`}
                      </span>
                    ) : null}
                    {draft.options.length > MIN_VENUE_OPTIONS ? (
                      <button
                        type="button"
                        className="admin-btn admin-btn-ghost admin-btn-sm !ml-auto !px-2"
                        onClick={() =>
                          setDraft({ ...draft, options: draft.options.filter((_, j) => j !== i) })
                        }
                      >
                        ✕
                      </button>
                    ) : null}
                  </div>
                ))}
                {draft.options.length < MAX_VENUE_OPTIONS ? (
                  <button
                    type="button"
                    className="admin-btn admin-btn-secondary admin-btn-sm"
                    onClick={() =>
                      setDraft({
                        ...draft,
                        options: [
                          ...draft.options,
                          { id: nextOptionId(draft.options), label: '', venueId: null, venue: null },
                        ],
                      })
                    }
                  >
                    Add venue
                  </button>
                ) : null}
                {duplicateVenue ? (
                  <div className="text-[11px] text-amber-400">
                    The same venue is on this card twice — two tiles for one bar split its vote.
                  </div>
                ) : null}
              </div>
            </Field>
          </div>
        ) : null}

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Who sees it" hint="A card for one city beats a worldwide card for readers in it, whatever the priorities say.">
            <select
              value={draft.cityId}
              onChange={(e) => setDraft({ ...draft, cityId: e.target.value })}
              className="admin-input"
            >
              <option value="">Everywhere</option>
              {cities.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="When" hint={hasSchedule ? 'Scheduled — see More options below.' : 'Open-ended: it stays up until you take it down.'}>
            <div className="flex h-[38px] items-center rounded-md border border-border bg-surfaceElevated px-3 text-[12px] text-muted">
              {hasSchedule ? `${fmtWhen(fromLocalInput(draft.startsAt))} → ${fmtWhen(fromLocalInput(draft.endsAt))}` : 'No window'}
            </div>
          </Field>
        </div>

        {/*
          ── THE RARE HALF ─────────────────────────────────────────────────────────────────────
          Eyebrow, link, priority and the schedule still exist and still do exactly what they did.
          They are behind one disclosure because the job this panel is for — "ask people something
          right now" — never touches them, and five more fields between the words and the button is
          what made the last version feel like paperwork. It opens ITSELF when you load a card that
          already uses one of them (see `editCard`), so editing a scheduled card never hides that
          card's own window; after that it is yours to open and close.
        */}
        <details
          className="mt-4 rounded-lg border border-border/80 bg-rowAlt/60 px-4 py-3"
          open={extrasOpen}
          onToggle={(e) => setExtrasOpen(e.currentTarget.open)}
        >
          <summary className="cursor-pointer select-none text-[12px] font-medium text-muted">
            More options — eyebrow, link, city priority, schedule
          </summary>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Eyebrow" hint="The small purple label above the copy. Optional, and usually better empty.">
              <input
                value={draft.eyebrow}
                onChange={(e) => setDraft({ ...draft, eyebrow: e.target.value })}
                maxLength={40}
                placeholder="Announcement"
                className="admin-input"
              />
            </Field>

            {draft.kind === 'question' ? (
              <Field label="Placeholder" hint="Greyed-out text inside their input, e.g. “Somewhere downtown…”">
                <input
                  value={draft.placeholder}
                  onChange={(e) => setDraft({ ...draft, placeholder: e.target.value })}
                  maxLength={60}
                  className="admin-input"
                />
              </Field>
            ) : null}

            <Field label="Link label" hint="Optional. Only drawn when there is a link too.">
              <input
                value={draft.ctaLabel}
                onChange={(e) => setDraft({ ...draft, ctaLabel: e.target.value })}
                maxLength={30}
                className="admin-input"
              />
            </Field>

            <Field label="Link" hint="Must start with http:// or https://">
              <input
                value={draft.ctaUrl}
                onChange={(e) => setDraft({ ...draft, ctaUrl: e.target.value })}
                className="admin-input"
                placeholder="https://"
              />
            </Field>

            <Field label="Priority" hint="Higher wins among cards of the same reach. Ties break on newest.">
              <input
                value={draft.priority}
                onChange={(e) => setDraft({ ...draft, priority: e.target.value.replace(/[^0-9-]/g, '') })}
                inputMode="numeric"
                className="admin-input"
              />
            </Field>

            <Field label="Starts" hint="Your local time. Blank means “as soon as it is on”.">
              <input
                type="datetime-local"
                value={draft.startsAt}
                onChange={(e) => setDraft({ ...draft, startsAt: e.target.value })}
                className="admin-input"
              />
            </Field>

            <Field label="Ends" hint="Your local time. Blank means “until you turn it off”.">
              <input
                type="datetime-local"
                value={draft.endsAt}
                onChange={(e) => setDraft({ ...draft, endsAt: e.target.value })}
                className="admin-input"
              />
            </Field>
          </div>
        </details>

        {/*
          ── ONE OBVIOUS BUTTON ────────────────────────────────────────────────────────────────
          ⚠️ THE CHECKBOX IS GONE. `is_active` still defaults to FALSE in the column — anything that
          writes a row without an opinion writes a draft — but the dashboard's primary button says
          TRUE out loud, because "type a question and have people see it" should not require
          noticing a tickbox first.
        */}
        {boardSetupNote === null ? (
          <label className="mt-5 flex cursor-pointer items-center gap-2.5 text-[12.5px] text-muted">
            <input
              type="checkbox"
              checked={draft.boardEnabled}
              onChange={(e) => setDraft((d) => ({ ...d, boardEnabled: e.target.checked }))}
              className="accent-accent"
            />
            <span>
              <span className="font-semibold text-textPrimary">Board</span> — readers can post
              replies under this card. Decide it here; you can still flip it later in the table.
            </span>
          </label>
        ) : null}
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button
            type="button"
            className="admin-btn admin-btn-primary"
            disabled={!ready}
            onClick={() => void save(true)}
          >
            {saving ? 'Saving…' : draft.id && draft.isActive ? 'Save changes' : 'Publish now'}
          </button>
          <button
            type="button"
            className="admin-btn admin-btn-secondary admin-btn-sm"
            disabled={!ready}
            onClick={() => void save(false)}
          >
            {draft.id && draft.isActive ? 'Save and take it down' : 'Save as draft'}
          </button>
          <span className="text-[11px] text-mutedDim">
            {draft.kind === 'poll' && filledOptions < 2
              ? 'A poll needs at least two options.'
              : draft.kind === 'venue_poll' && duplicateVenue
                ? 'Two tiles point at the same venue.'
                : draft.kind === 'venue_poll' && pickedVenues < MIN_VENUE_OPTIONS
                  ? `A venue poll needs at least ${MIN_VENUE_OPTIONS} venues.`
                  : draft.body.trim() === ''
                    ? 'Type something first.'
                    : hasSchedule
                      ? 'It goes up when its window opens.'
                      : 'It is on the search page the moment you press this.'}
          </span>
        </div>
      </section>

      <div className="mb-6 grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        <StatCard label="Showing now" value={loading ? null : liveCount} sub="On and inside its window" />
        <StatCard label="Scheduled / expired" value={loading ? null : scheduledCount} sub="On, but outside its window" />
        <StatCard label="Cards" value={loading ? null : cards.length} sub="Everything written, live or not" />
        <StatCard label="Answers" value={loading ? null : answerCount} sub="Votes and typed replies" />
        {/* ⚠️ "On the boards", not "posted" — a hidden message is still a message and is not here. */}
        <StatCard
          label="Board messages"
          value={loading ? null : boardMessageCount}
          sub="On the boards now, hidden ones excluded"
        />
      </div>

      {/*
        ── THE LEGEND, BECAUSE A COLUMN HEADING HOLDS ONE WORD ──────────────────────────────────
        ⚠️ **Tonight** IS NOT ONLY A RESET, AND THIS IS THE ONLY PLACE THAT CAN SAY SO. The column
        is one word wide; the switch behind it changes three things at once (the 8am ET reset, what
        the board shows, and the chip under the app's search bar). Someone reading this table in
        three weeks has to be able to find that out without opening the source, or they will flip it
        to change a reset and quietly take the Tonight chip off the search page with it.
      */}
      {!loading && cards.length > 0 ? (
        <div className="mb-4 max-w-3xl space-y-1 text-[11px] leading-relaxed text-mutedDim">
          <p>
            <span className="font-semibold text-muted">Tonight</span> — this card is about tonight.
            Its answers and its board clear at <span className="text-muted">8am Eastern</span>, and
            while it is live the app shows a <span className="text-muted">Tonight</span> chip under
            the search bar in place of <span className="text-muted">Top rated</span>. One switch,
            all three.
          </p>
          {/*
            ⚠️ SAID OUT LOUD BECAUSE THE TWO NUMBERS REALLY DO DISAGREE, and the honest place to say
            so is next to the switch that causes it. `_search_spotlight_payload` scopes every tally
            it sends the phone with `(not c.resets_daily or r.poll_day = v_day)`;
            `admin_list_spotlight_cards.response_count` is a bare `count(*)` over the card's whole
            life. Both are correct about different questions. Narrowing the admin one changes a
            number Evan reads, so it is his call — OUTSTANDING 66-B5 — and until he makes it, a
            sentence beats a silently inflated column.
          */}
          <p>
            One mismatch to know about: on a Tonight card the{' '}
            <span className="text-muted">Answers</span> column and the{' '}
            <span className="text-muted">Answers</span> panel still total{' '}
            <em className="not-italic text-muted">every</em> night, while the app shows tonight
            only — so the dashboard reads higher.
          </p>
          <p>
            <span className="font-semibold text-muted">Board</span> — readers can post under the
            card. Off by default. They choose per message whether to sign it; either way you see who
            wrote it, under <span className="text-muted">Board</span> in the last column.
          </p>
        </div>
      ) : null}
      {boardSetupNote ? <div className="mb-4"><SetupNote>{boardSetupNote}</SetupNote></div> : null}

      {/* ── The table ────────────────────────────────────────────────────────────────────── */}
      {loading ? (
        <SkeletonRows rows={6} />
      ) : cards.length === 0 ? (
        <EmptyState
          title="No spotlight cards yet"
          hint={
            setupNote
              ? 'Apply the migration and this panel comes alive.'
              : 'The slot on the app’s Search tab is empty — it renders nothing at all until you publish something above.'
          }
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="admin-table">
            <thead>
              <tr>
                <th className="px-4 py-3 text-left">Card</th>
                <th className="px-4 py-3 text-left">Kind</th>
                <th className="px-4 py-3 text-left">Where</th>
                <th className="px-4 py-3 text-left">Window</th>
                <th className="px-4 py-3 text-right">Priority</th>
                <th className="px-4 py-3 text-right">Answers</th>
                <th className="px-4 py-3 text-left">Live</th>
                <th className="px-4 py-3 text-left">Tonight</th>
                <th className="px-4 py-3 text-left">Board</th>
                <th className="px-4 py-3 text-left" />
              </tr>
            </thead>
            <tbody>
              {cards.map((c, i) => (
                <tr key={c.id} className={`border-b border-border/50 ${i % 2 === 0 ? 'bg-surface' : 'bg-rowAlt'}`}>
                  <td className="max-w-sm px-4 py-4 align-top">
                    {c.eyebrow ? <div className="text-[11px] text-accent">{c.eyebrow}</div> : null}
                    <div className="admin-label-primary leading-snug">{c.body}</div>
                    {/* ⚠️ "On but not showing" is the state a table without a window column hides. */}
                    {c.is_active && !c.is_live ? (
                      <div className="mt-1 text-[10px] text-amber-400">
                        On, but outside its window — nobody sees this right now.
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-4 align-top text-sm text-muted">{KIND_LABEL[c.kind] ?? c.kind}</td>
                  <td className="px-4 py-4 align-top text-[12px] text-muted">{c.city_name ?? 'Everywhere'}</td>
                  <td className="px-4 py-4 align-top text-[11px] leading-relaxed text-mutedDim">
                    {c.starts_at || c.ends_at ? (
                      <>
                        {fmtWhen(c.starts_at)} → {fmtWhen(c.ends_at)}
                      </>
                    ) : (
                      'Always'
                    )}
                  </td>
                  <td className="px-4 py-4 align-top text-right admin-metric tabular-nums">{c.priority}</td>
                  <td className="px-4 py-4 align-top text-right admin-metric tabular-nums">{c.response_count}</td>
                  <td className="px-4 py-4 align-top">
                    <ToggleSwitch
                      on={c.is_active}
                      disabled={disabled}
                      label={`${c.body} live on the search tab`}
                      onToggle={() => void toggleLive(c, !c.is_active)}
                    />
                  </td>
                  {/*
                    ⚠️ THE `title` NAMES ALL THREE EFFECTS. The heading says "Tonight" and the legend
                    above the table explains it once, but the one place somebody will actually
                    hover is the control itself. See {@link CardRow.resets_daily}.
                  */}
                  <td className="px-4 py-4 align-top">
                    <ToggleSwitch
                      on={c.resets_daily}
                      disabled={disabled}
                      label={`${c.body} is about tonight — resets at 8am Eastern and shows the Tonight chip`}
                      title={
                        'This card is about tonight: its answers and its board clear at 8am Eastern, ' +
                        'and the app shows a "Tonight" chip under the search bar instead of "Top rated".'
                      }
                      onToggle={() => void setFlags(c, { resetsDaily: !c.resets_daily })}
                    />
                  </td>
                  <td className="px-4 py-4 align-top">
                    <ToggleSwitch
                      on={c.board_enabled}
                      disabled={disabled}
                      label={`${c.body} public message board`}
                      title={
                        'Readers can post a message under this card. They choose whether to sign it; ' +
                        'you can always see who wrote it. Hiding a message is reversible.'
                      }
                      onToggle={() => void setFlags(c, { boardEnabled: !c.board_enabled })}
                    />
                    {/* The count only earns its place when it is not zero. */}
                    {c.board_count > 0 ? (
                      <div className="mt-1 text-[10px] tabular-nums text-mutedDim">
                        {c.board_count} message{c.board_count === 1 ? '' : 's'}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-4 py-4 align-top">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="admin-btn admin-btn-secondary admin-btn-sm"
                        disabled={disabled}
                        onClick={() => void editCard(c)}
                      >
                        Edit
                      </button>
                      {c.kind === 'poll' || c.kind === 'venue_poll' || c.kind === 'question' ? (
                        <button
                          type="button"
                          className="admin-btn admin-btn-ghost admin-btn-sm"
                          disabled={disabled}
                          onClick={() => void openResults(c)}
                        >
                          Answers
                        </button>
                      ) : null}
                      {/*
                        ⚠️ SHOWN ON EVERY ROW, INCLUDING CARDS WHOSE BOARD IS OFF. Gating it on
                        `board_enabled || board_count > 0` reads tidier and hides the one case that
                        needs it most: a board switched off with messages still inside it, all of
                        them hidden, so the count is 0 and there is no way back to press Restore.
                        `board_count` excludes hidden rows by definition — see {@link CardRow}.
                      */}
                      <button
                        type="button"
                        className="admin-btn admin-btn-ghost admin-btn-sm"
                        disabled={disabled}
                        onClick={() => setBoardCardId((prev) => (prev === c.id ? null : c.id))}
                      >
                        {boardCardId === c.id ? 'Hide board' : 'Board'}
                        {c.board_count > 0 ? ` · ${c.board_count}` : ''}
                      </button>
                      <button
                        type="button"
                        className="admin-btn admin-btn-ghost admin-btn-sm"
                        disabled={disabled}
                        onClick={() => setPendingDelete(c)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/*
        ── ONE CARD'S BOARD ──────────────────────────────────────────────────────────────────────
        ⚠️ DIRECTLY UNDER THE TABLE, NOT DOWN WITH "ANSWERS". Moderation is the one thing on this
        page you do in a hurry, and the button that opens it is in the row immediately above — put
        the preview between them and every takedown starts with a scroll to find out whether the
        click worked.

        ⚠️ `key={boardCard.id}` — the panel holds a card's messages, a filter and a poll day in its
        own state. Without the key, opening a second card's board would re-use the first one's
        mounted instance and show yesterday's list until the fetch returned.
      */}
      {boardCard ? (
        <SpotlightBoardPanel
          key={boardCard.id}
          cardId={boardCard.id}
          cardBody={boardCard.body}
          boardEnabled={boardCard.board_enabled}
          resetsDaily={boardCard.resets_daily}
          onClose={() => setBoardCardId(null)}
          /* The table's `board_count` is the server's number; a hide has to make it re-ask. */
          onModerated={() => void refreshAll()}
        />
      ) : null}

      {/* ── The preview: the single card one city actually gets ───────────────────────────── */}
      <section className="mt-8">
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <h2 className="admin-section-label">What a reader receives</h2>
          <label className="flex shrink-0 items-center gap-2 text-xs text-muted">
            <span className="text-mutedDim">City</span>
            <select
              value={cityId}
              onChange={(e) => selectCity(e.target.value)}
              disabled={cities.length === 0}
              className="admin-input !w-auto !px-2 !text-xs"
            >
              {cities.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <span className="rounded-full border border-border/80 bg-white/[0.04] px-3 py-1 text-[11px] text-muted">
            get_search_spotlight — the exact call the app makes
          </span>
        </div>

        {previewBusy ? (
          <SkeletonRows rows={2} />
        ) : preview ? (
          <div className="max-w-md rounded-xl border border-border bg-surface p-4">
            <div className="mb-2 flex items-center gap-2">
              {preview.eyebrow ? (
                <span className="rounded-full bg-accent/15 px-2.5 py-1 text-[11px] font-semibold text-accent">
                  {preview.eyebrow}
                </span>
              ) : null}
              <span className="text-[10px] uppercase tracking-wide text-mutedDim">
                {KIND_LABEL[preview.kind] ?? preview.kind}
              </span>
            </div>
            <div className="text-[14px] font-medium leading-snug text-textPrimary">{preview.body}</div>
            {Array.isArray(preview.options) && preview.options.length > 0 ? (
              <ul className="mt-3 space-y-1.5">
                {preview.options.map((o, i) => (
                  <li
                    key={o.id ?? i}
                    className="flex items-center gap-2 rounded-md border border-border/70 bg-rowAlt px-3 py-1.5 text-[12px] text-muted"
                  >
                    {/*
                      ⚠️ THE TILE'S ACTUAL PHOTO, AND A BLANK SQUARE WHEN THERE IS NONE — which is
                      exactly what the phone draws (a tinted placeholder, not a broken image). The
                      point of this preview is to catch a card full of photoless venues BEFORE it
                      is live, so the empty square has to be visible rather than collapsed away.
                    */}
                    {o.venue_id ? (
                      o.photo_url ? (
                        <img
                          src={o.photo_url}
                          alt=""
                          className="h-8 w-8 shrink-0 rounded object-cover"
                        />
                      ) : (
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-white/[0.06] text-[10px] text-mutedDim">
                          no photo
                        </span>
                      )
                    ) : null}
                    <span className="min-w-0 flex-1 truncate">{o.label}</span>
                    <span className="shrink-0 tabular-nums text-mutedDim">{o.count ?? 0}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : (
          <EmptyState
            title={`Nothing shows in ${cityName ?? 'this city'}`}
            hint="No card is live here, so the app renders nothing at all in that slot. Publish something above and it appears."
            compact
          />
        )}
      </section>

      {/* ── One card's answers ───────────────────────────────────────────────────────────── */}
      {results ? (
        <section className="mt-8 rounded-xl border border-border bg-surface p-5">
          <div className="mb-3 flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h2 className="admin-label-primary text-base">{results.body}</h2>
              <div className="text-[11px] text-mutedDim">
                {results.total} answer{results.total === 1 ? '' : 's'}
              </div>
            </div>
            <button type="button" className="admin-btn admin-btn-ghost admin-btn-sm" onClick={closeResults}>
              Close
            </button>
          </div>

          {Array.isArray(results.options) && results.options.length > 0 ? (
            <ul className="mb-4 space-y-1.5">
              {results.options.map((o, i) => {
                const count = o.count ?? 0;
                const pct = results.total > 0 ? Math.round((count / results.total) * 100) : 0;
                return (
                  <li key={o.id ?? i} className="rounded-md border border-border/70 bg-rowAlt px-3 py-2">
                    <div className="flex items-center justify-between text-[12px] text-muted">
                      <span className="truncate">{o.label}</span>
                      <span className="tabular-nums text-mutedDim">
                        {count} · {pct}%
                      </span>
                    </div>
                    <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-black/40">
                      <div className="h-full rounded-full bg-accent" style={{ width: `${pct}%` }} />
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : null}

          {/*
            ── THE MORNING AFTER ────────────────────────────────────────────────────────────────
            Evan: *"make it where I can send notifications the next morning easily to those that
            indicated they rated certain places. reminding them to rate it."*

            ⚠️ IT LIVES UNDER **ANSWERS**, NOT IN THE COMPOSER, because it is a thing you do TO a
            card that already has answers — the composer is for writing, and putting a button that
            pushes to real phones next to "Publish now" is asking for the wrong one to be pressed.

            ⚠️ AN ANNOUNCEMENT CARD NEVER SHOWS THIS. It has no options, so nothing can be linked
            and nobody could be reminded of anything.
          */}
          {results.kind === 'poll' || results.kind === 'venue_poll' ? (
            <div className="mb-4 rounded-lg border border-border/80 bg-rowAlt/60 p-4">
              <div className="mb-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <h3 className="admin-label-primary text-[13px]">Remind them to rate it</h3>
                <span className="text-[11px] leading-relaxed text-mutedDim">
                  {reminderNote
                    ? reminderNote
                    : reminderAudience === null
                      ? 'Working out who…'
                      : linkedOptionCount === 0
                        ? 'No option on this card is linked to a venue — link one in the composer above and nobody has to retype anything.'
                        : reminderCount === 0
                          ? 'Nobody qualifies right now — everyone who picked a linked venue has either rated it since or already been reminded.'
                          : `${reminderCount} ${reminderCount === 1 ? 'person' : 'people'} would be reminded.`}
                </span>
              </div>

              {/* The number, explained. A count you cannot break down is a number you cannot trust. */}
              {reminderByVenue.length > 0 ? (
                <ul className="mb-3 flex flex-wrap gap-2">
                  {reminderByVenue.map(([venue, n]) => (
                    <li
                      key={venue}
                      className="rounded-full border border-border/80 bg-white/[0.04] px-3 py-1 text-[11px] text-muted"
                    >
                      {venue} · <span className="tabular-nums text-textPrimary">{n}</span>
                    </li>
                  ))}
                </ul>
              ) : null}

              {linkedOptionCount > 0 ? (
                <>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field
                      label="Headline"
                      hint={`Optional. ${reminderTitle.length}/${REMINDER_TITLE_MAX}. Use {venue} and each person sees their own bar.`}
                    >
                      <input
                        value={reminderTitle}
                        onChange={(e) =>
                          setReminderTitle(e.target.value.slice(0, REMINDER_TITLE_MAX))
                        }
                        className="admin-input"
                        placeholder={DEFAULT_REMINDER_TITLE}
                      />
                    </Field>
                    <Field
                      label="Message"
                      hint={`${reminderBody.length}/${REMINDER_BODY_MAX}. {venue} works here too.`}
                    >
                      <input
                        value={reminderBody}
                        onChange={(e) =>
                          setReminderBody(e.target.value.slice(0, REMINDER_BODY_MAX))
                        }
                        className="admin-input"
                        placeholder={DEFAULT_REMINDER_BODY}
                      />
                    </Field>
                  </div>

                  {/*
                    ⚠️ ONE LINE, NOT TWO, AND THE TITLE IS ALWAYS "shado". Every push this app has
                    ever sent uses that title — it is hardcoded in the send-push-notification Edge
                    Function — so the headline field is joined onto the message rather than
                    becoming an iOS title. Showing the real thing here is the only way that is
                    obvious before it is sent to somebody's lock screen.
                  */}
                  <div className="mt-3 max-w-md rounded-md border border-border bg-surface px-3 py-2">
                    <div className="text-[10px] uppercase tracking-wide text-mutedDim">
                      What lands on their phone
                    </div>
                    <div className="mt-1 text-[12px] font-semibold text-textPrimary">shado</div>
                    <div className="text-[12px] leading-snug text-muted">
                      {reminderPreview || '—'}
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      className="admin-btn admin-btn-primary admin-btn-sm"
                      disabled={
                        reminderBusy ||
                        reminderCount === 0 ||
                        reminderBody.trim() === '' ||
                        reminderNote !== null
                      }
                      onClick={() => setConfirmSend(true)}
                    >
                      {reminderBusy
                        ? 'Working…'
                        : `Send to ${reminderCount} ${reminderCount === 1 ? 'person' : 'people'}`}
                    </button>
                    <button
                      type="button"
                      className="admin-btn admin-btn-ghost admin-btn-sm"
                      disabled={reminderBusy}
                      onClick={() => void loadReminderAudience(results.id)}
                    >
                      Recount
                    </button>
                    <span className="text-[11px] text-mutedDim">
                      {reminderSent !== null
                        ? `${reminderSent} sent. Pressing it again only reaches people who answered since.`
                        : reminderBody.trim() === ''
                          ? 'Type a message first.'
                          : 'Nobody can be reminded twice for this card.'}
                    </span>
                  </div>
                </>
              ) : null}
            </div>
          ) : null}

          {Array.isArray(results.free_text) && results.free_text.length > 0 ? (
            <ul className="divide-y divide-border/50 overflow-hidden rounded-lg border border-border">
              {results.free_text.map((r, i) => (
                <li key={`${r.user_id ?? i}`} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-3 py-2">
                  <span className="text-[12px] text-textPrimary">{r.free_text}</span>
                  <span className="ml-auto text-[11px] text-mutedDim">
                    {r.username ? `@${r.username}` : 'deleted account'} · {fmtWhen(r.updated_at ?? null)}
                  </span>
                </li>
              ))}
            </ul>
          ) : results.kind === 'question' ? (
            <EmptyState title="Nobody has answered yet" compact />
          ) : null}
        </section>
      ) : null}

      <ConfirmModal
        open={pendingDelete !== null}
        title="Delete this card?"
        message={
          pendingDelete
            ? `“${pendingDelete.body}” and its ${pendingDelete.response_count} answer${
                pendingDelete.response_count === 1 ? '' : 's'
              } are removed. This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        danger
        busy={saving}
        onConfirm={() => void doDelete()}
        onCancel={() => setPendingDelete(null)}
      />

      {/*
        ⚠️ A CONFIRM STEP, BECAUSE THIS ONE LEAVES THE BUILDING. Every other button on this page
        writes a row Evan can toggle back off; this one buzzes real phones and cannot be recalled.
        The message names the count and quotes the exact sentence they will read.
      */}
      <ConfirmModal
        open={confirmSend && results !== null}
        title="Send this reminder?"
        message={
          `${reminderCount} ${reminderCount === 1 ? 'person' : 'people'} get a push saying ` +
          `“${reminderPreview}”, with each one's own venue filled in. It cannot be taken back, ` +
          `and nobody can be reminded twice for this card.`
        }
        confirmLabel="Send"
        busy={reminderBusy}
        onConfirm={() => void sendReminders()}
        onCancel={() => setConfirmSend(false)}
      />
    </div>
  );
}
